import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, CheckCheck, Trash2, Bell, AlertTriangle, Info, AlertCircle, FileText } from 'lucide-react';

const typeIcons = {
  info: { icon: Info, bg: 'bg-blue-50', color: 'text-blue-500' },
  warning: { icon: AlertTriangle, bg: 'bg-amber-50', color: 'text-amber-500' },
  error: { icon: AlertCircle, bg: 'bg-red-50', color: 'text-red-500' },
  performance_alert: { icon: Bell, bg: 'bg-indigo-50', color: 'text-indigo-500' },
  daily_summary: { icon: FileText, bg: 'bg-emerald-50', color: 'text-emerald-500' },
  weekly_summary: { icon: FileText, bg: 'bg-teal-50', color: 'text-teal-500' },
  monthly_summary: { icon: FileText, bg: 'bg-cyan-50', color: 'text-cyan-500' },
};

function getTimeAgo(dateStr) {
  const now = new Date();
  const date = new Date(dateStr);
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

export function NotificationDrawer({
  notifications, isOpen, onClose,
  onMarkRead, onMarkAllRead, onDelete,
  unreadCount, isLoading,
  onCreateDaily, onCreateWeekly, onCreateMonthly,
}) {
  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/20 backdrop-blur-sm z-50"
          />

          {/* Drawer */}
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 30, stiffness: 300 }}
            className="fixed right-0 top-0 bottom-0 w-full sm:w-[420px] z-50 border-l border-gray-200 flex flex-col"
            style={{ background: 'rgba(255,255,255,0.97)', backdropFilter: 'blur(20px)' }}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
              <div className="flex items-center gap-2">
                <Bell size={18} className="text-gray-700" />
                <h2 className="text-sm font-semibold text-gray-800">Notifications</h2>
                {unreadCount > 0 && (
                  <span className="bg-red-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full">{unreadCount}</span>
                )}
              </div>
              <div className="flex items-center gap-2">
                {unreadCount > 0 && (
                  <button
                    onClick={onMarkAllRead}
                    className="text-[11px] text-blue-500 hover:text-blue-700 font-medium flex items-center gap-1"
                  >
                    <CheckCheck size={14} /> Mark all read
                  </button>
                )}
                <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors">
                  <X size={16} className="text-gray-400" />
                </button>
              </div>
            </div>

            {/* Summary generation buttons */}
            <div className="flex gap-2 px-5 py-3 border-b border-gray-50">
              <button onClick={onCreateDaily} className="text-[10px] px-3 py-1.5 rounded-lg bg-blue-50 text-blue-600 font-semibold hover:bg-blue-100 transition-colors">
                Daily Summary
              </button>
              <button onClick={onCreateWeekly} className="text-[10px] px-3 py-1.5 rounded-lg bg-indigo-50 text-indigo-600 font-semibold hover:bg-indigo-100 transition-colors">
                Weekly Summary
              </button>
              <button onClick={onCreateMonthly} className="text-[10px] px-3 py-1.5 rounded-lg bg-purple-50 text-purple-600 font-semibold hover:bg-purple-100 transition-colors">
                Monthly Summary
              </button>
            </div>

            {/* Notification list */}
            <div className="flex-1 overflow-y-auto">
              {isLoading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full" />
                </div>
              ) : notifications.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-gray-400">
                  <Bell size={32} className="mb-2 opacity-30" />
                  <p className="text-xs">No notifications</p>
                </div>
              ) : (
                <div className="p-2">
                  {notifications.map((notif) => {
                    const typeConfig = typeIcons[notif.type] || typeIcons.info;
                    const TypeIcon = typeConfig.icon;
                    const timeAgo = notif.createdAt ? getTimeAgo(notif.createdAt) : '';

                    return (
                      <motion.div
                        key={notif.id}
                        layout
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, x: 50 }}
                        className={`group p-3 rounded-xl mb-1.5 transition-colors cursor-pointer ${
                          notif.is_read ? 'bg-transparent hover:bg-gray-50' : 'bg-blue-50/50 hover:bg-blue-50'
                        }`}
                        onClick={() => !notif.is_read && onMarkRead(notif.id)}
                      >
                        <div className="flex gap-3">
                          <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${typeConfig.bg}`}>
                            <TypeIcon size={14} className={typeConfig.color} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between gap-2">
                              <p className={`text-xs font-medium truncate ${notif.is_read ? 'text-gray-600' : 'text-gray-900'}`}>
                                {notif.title}
                              </p>
                              <button
                                onClick={(e) => { e.stopPropagation(); onDelete(notif.id); }}
                                className="p-1 rounded hover:bg-red-50 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity"
                              >
                                <Trash2 size={12} className="text-gray-300 hover:text-red-400" />
                              </button>
                            </div>
                            <p className="text-[11px] text-gray-500 mt-0.5 line-clamp-2 whitespace-pre-line">{notif.message}</p>
                            <div className="flex items-center gap-2 mt-1.5">
                              <span className="text-[10px] text-gray-400">{timeAgo}</span>
                              {!notif.is_read && (
                                <span className="w-1.5 h-1.5 rounded-full bg-blue-500" />
                              )}
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    );
                  })}
                </div>
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
